import { DatePicker } from '@/frontend/components/date-picker'
import { type SnapInterval, TimePicker } from '@/frontend/components/time-picker'
import { cn } from '@/frontend/lib/utils'

interface DateTimePickerProps {
  value?: Date
  onChange?: (date: Date) => void
  snap?: SnapInterval
  /** Time minimum in "HH:mm" format */
  min?: string
  /** Time maximum in "HH:mm" format */
  max?: string
  placeholder?: string
  disabled?: boolean
  className?: string
}

/** Extract "HH:mm" string from a Date */
function getTimeFromDate(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/** Merge a date and "HH:mm" time string into a new Date */
function mergeDateAndTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)
  const merged = new Date(date)
  merged.setHours(hours, minutes, 0, 0)
  return merged
}

function DateTimePicker({
  value,
  onChange,
  snap = 15,
  min = '00:00',
  max = '23:59',
  placeholder = 'Select date & time',
  disabled,
  className,
}: DateTimePickerProps) {
  const currentDate = value ?? new Date()
  const currentTime = getTimeFromDate(currentDate)

  function handleDateChange(newDate: Date | undefined) {
    const date = newDate ?? new Date()
    onChange?.(mergeDateAndTime(date, currentTime))
  }

  function handleTimeChange(newTime: string) {
    onChange?.(mergeDateAndTime(currentDate, newTime))
  }

  return (
    <div className={cn('flex w-full gap-2', className)}>
      <DatePicker
        value={value}
        onChange={handleDateChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1"
      />
      <TimePicker
        value={currentTime}
        onChange={handleTimeChange}
        snap={snap}
        min={min}
        max={max}
        disabled={disabled}
        className="w-[120px]"
      />
    </div>
  )
}

export { DateTimePicker, type DateTimePickerProps, type SnapInterval }
